type ADFNode = {
  type: string
  attrs: { level: number; href: string }
  content?: ADFNode[]
  marks?: { type: string; attrs: { href: string } }[]
  text: string
}

function convertNodeToMarkdown(node: ADFNode, indentLevel: number): string {
  const indent = '  '.repeat(indentLevel)

  switch (node.type) {
    case 'heading':
      return `${'#'.repeat(node.attrs.level)} ${node.content?.[0].text || ''}`

    case 'paragraph':
      return (
        node.content
          ?.map((contentNode) => {
            if (contentNode.type === 'text') {
              if (contentNode.marks?.some((mark) => mark.type === 'link')) {
                const linkMark = contentNode.marks.find((mark) => mark.type === 'link')
                return `[${contentNode.text}](${linkMark?.attrs.href})`
              }
              return contentNode.text
            }
            return ''
          })
          .join('') || ''
      )

    case 'bulletList':
      return (
        node.content
          ?.map((listItem) => {
            // listItem.content is an array of other block nodes.
            // Process them and join with a newline.
            const itemText =
              listItem.content
                ?.map((contentNode) => convertNodeToMarkdown(contentNode, indentLevel + 1))
                .join('\n') || ''
            return `${indent}* ${itemText}`
          })
          .join('\n') || ''
      )

    default:
      return ''
  }
}

export function convertAdfToMarkdown(adf: unknown[]): string {
  const nodes = adf as ADFNode[]
  return nodes.map((node) => convertNodeToMarkdown(node, 0)).join('\n\n')
}

export function addHeading(adfContent: unknown[], level: number, text: string) {
  adfContent.push({
    type: 'heading',
    attrs: { level },
    content: [{ type: 'text', text }],
  })
}

export function addParagraph(adfContent: unknown[], text: string) {
  adfContent.push({
    type: 'paragraph',
    content: [{ type: 'text', text }],
  })
}

export function addLinkParagraph(adfContent: unknown[], text: string, href: string) {
  adfContent.push({
    type: 'paragraph',
    content: [
      {
        type: 'text',
        text,
        marks: [{ type: 'link', attrs: { href } }],
      },
    ],
  })
}

export function addBulletList(adfContent: unknown[], items: (string | Record<string, string[]>)[]) {
  if (!items || items.length === 0) {
    return
  }

  const listContent = items.map((item) => {
    if (typeof item === 'string') {
      return {
        type: 'listItem',
        content: [{ type: 'paragraph', content: [{ type: 'text', text: item }] }],
      }
    } else {
      const [key, subItems] = Object.entries(item)[0]
      const listItemContent: unknown[] = [
        { type: 'paragraph', content: [{ type: 'text', text: key }] },
      ]

      if (subItems && subItems.length > 0) {
        listItemContent.push({
          type: 'bulletList',
          content: subItems.map((subItem) => ({
            type: 'listItem',
            content: [{ type: 'paragraph', content: [{ type: 'text', text: subItem }] }],
          })),
        })
      }

      return {
        type: 'listItem',
        content: listItemContent,
      }
    }
  })

  adfContent.push({
    type: 'bulletList',
    content: listContent,
  })
}
